import type { MaybeRefOrGetter, Ref } from 'vue'

// Cards become a carousel below Girassol's `md:system` breakpoint (1024 px).
const CAROUSEL_MEDIA_QUERY = '(max-width: 1023px)'
const REDUCED_MOTION_MEDIA_QUERY = '(prefers-reduced-motion: reduce)'

/**
 * Horizontal track using CSS scroll-snap (the snap itself lives in the consumer's CSS).
 * Opens centered on `startIndex`, also when the layout switches from grid to carousel.
 */
export function useSnapCarousel(track: Readonly<Ref<HTMLElement | null>>, startIndex: MaybeRefOrGetter<number>) {
  const currentIndex = ref(toValue(startIndex))

  const carouselMedia = window.matchMedia(CAROUSEL_MEDIA_QUERY)
  const reducedMotionMedia = window.matchMedia(REDUCED_MOTION_MEDIA_QUERY)

  function isScrollable(trackElement: HTMLElement) {
    return trackElement.scrollWidth > trackElement.clientWidth
  }

  function itemAt(index: number) {
    return track.value?.children[index] as HTMLElement | undefined
  }

  function scrollToIndex(index: number, behavior: ScrollBehavior) {
    const trackElement = track.value
    const item = itemAt(index)
    if (!trackElement || !item || !isScrollable(trackElement))
      return
    trackElement.scrollTo({ left: item.offsetLeft - (trackElement.clientWidth - item.offsetWidth) / 2, behavior })
  }

  // Closest item to the track center, from the real scroll position. Without scroll (grid), `startIndex` wins.
  function syncCurrentIndex() {
    const trackElement = track.value
    if (!trackElement || !isScrollable(trackElement)) {
      currentIndex.value = toValue(startIndex)
      return
    }
    const trackCenter = trackElement.scrollLeft + trackElement.clientWidth / 2
    const distances = Array.from(trackElement.children, (child) => {
      const item = child as HTMLElement
      return Math.abs(item.offsetLeft + item.offsetWidth / 2 - trackCenter)
    })
    currentIndex.value = distances.indexOf(Math.min(...distances))
  }

  function openAtStart() {
    scrollToIndex(toValue(startIndex), 'instant')
    // After layout: the position may have been clamped by the track width.
    requestAnimationFrame(syncCurrentIndex)
  }

  function goTo(index: number) {
    currentIndex.value = index
    scrollToIndex(index, reducedMotionMedia.matches ? 'auto' : 'smooth')
  }

  function handleCarouselMediaChange() {
    nextTick(openAtStart)
  }

  watch(() => toValue(startIndex), () => nextTick(openAtStart))

  onMounted(openAtStart)

  carouselMedia.addEventListener('change', handleCarouselMediaChange)
  onScopeDispose(() => carouselMedia.removeEventListener('change', handleCarouselMediaChange))

  return { currentIndex, goTo, handleScroll: syncCurrentIndex }
}
